import {
  Paper,
  Skeleton,
  Stack,
  Table as MUITable,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../contexes/AuthContext';
import DashboardContext from '../../../contexes/DashboardContext';
import { useAgentsWithKnowledge } from '../../../hooks/queries/useAgentsWithKnowledge';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentWithAttachedKnowledge } from '../../../interfaces/Agent';
import StyledTableColumnHeader from '../../shared/Table/StyledTableColumnHeader';
import Text from '../../shared/Text/Text';
import TableItem from './TableItem';

const Table = (): JSX.Element => {
  const { t } = useTranslation();
  const { setAgentsCount } = useContext(DashboardContext);
  const { currentUser } = useContext(AuthContext);
  const displayMessage = useSnackBar();
  const navigate = useNavigate();

  // Use React Query to fetch agents with knowledge
  const {
    data: agents,
    isLoading,
    error,
    agentsCount,
  } = useAgentsWithKnowledge(
    currentUser?.id,
    !!currentUser // Only fetch when user is authenticated
  );

  // Update agents count when data changes
  useEffect(() => {
    setAgentsCount(agentsCount);
  }, [agentsCount, setAgentsCount]);

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error(error);
      displayMessage(
        t('Something went wrong, please reload the page.'),
        'error'
      );
    }
  }, [error, displayMessage, t]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
        <Skeleton variant="rounded" height={60} />
      </Stack>
    );
  }

  if (!agents || agents.length === 0) {
    return (
      <Stack textAlign="center">
        <Text type="subtitle2">{t('No call agents found.')}</Text>
      </Stack>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: '100%',
        overflowX: 'auto',
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: 'none',
        bgcolor: '#FFFFFF',
      }}
    >
      <MUITable
        sx={{
          width: '100%',
          tableLayout: 'auto',
          '& .MuiTableHead-root .MuiTableRow-root': {
            bgcolor: '#FFFFFF',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#5A6FAF',
            fontSize: '0.78rem',
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: '0.01em',
            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
            py: 1.5,
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            transition: 'background-color 120ms ease',
            '&:hover': {
              bgcolor: '#F9FBFF',
            },
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            borderBottom: '1px solid rgba(17, 24, 39, 0.06)',
            py: 1.25,
          },
        }}
        aria-label={t('Call agents table')}
      >
        <TableHead>
          <TableRow>
            <StyledTableColumnHeader transform="uppercase" fontWeight="bold">
              {t('Agent')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader
              transform="uppercase"
              align="center"
              fontWeight="bold"
            >
              {t('Role')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader
              transform="uppercase"
              align="center"
              fontWeight="bold"
            >
              {t('Visibility')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader
              transform="uppercase"
              align="center"
              fontWeight="bold"
            >
              {t('Knowledge')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader
              transform="uppercase"
              align="center"
              fontWeight="bold"
            >
              {t('Number')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader
              transform="uppercase"
              align="center"
              fontWeight="bold"
            >
              {t('Status')}
            </StyledTableColumnHeader>
            <StyledTableColumnHeader transform="uppercase" align="right">
              {/* Intentionally left blank. */}
            </StyledTableColumnHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.map((agent: AgentWithAttachedKnowledge, index) => (
            <TableItem key={agent.id || index} agent={agent} agents={agents} />
          ))}
        </TableBody>
      </MUITable>
    </TableContainer>
  );
};

export default Table;
